const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || "";
const ID_WEB_URL = process.env.REACT_APP_ID_WEB_URL || "";
const ID_LOGIN_URL =
    process.env.REACT_APP_ID_LOGIN_URL ||
    (ID_WEB_URL ? `${ID_WEB_URL.replace(/\/$/, "")}/login` : "/login");
const DEFAULT_HASH_PATH = "/news";
const BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 = "cookie-only-v1";
let accessToken: string | null = null;

type RefreshPayload = {
    access_token?: string;
    token?: string;
};

function setAccessTokenFromPayload(payload: RefreshPayload | null): void {
    const token = payload?.access_token || payload?.token || "";
    accessToken = token || null;
}

export function getIdAccessToken(): string {
    return accessToken || "";
}

export function getFingerprintFromCookie(): string {
    try {
        const match = document.cookie.match(/(?:^|;\s*)fp=([^;]+)/);
        return match ? decodeURIComponent(match[1]) : "";
    } catch {
        return "";
    }
}

export function getCookieCsrfHeaders(method?: string): Record<string, string> {
    if (!["post", "put", "patch", "delete"].includes((method ?? "get").toLowerCase())) {
        return {};
    }

    const fingerprint = getFingerprintFromCookie();
    return fingerprint ? { "X-CSRF-Token": fingerprint } : {};
}

function isLoginHash(hashValue: string): boolean {
    const hash = hashValue.startsWith("#") ? hashValue.slice(1) : hashValue;
    return hash === "/login" || hash.startsWith("/login?");
}

export function getDefaultReturnToUrl(): string {
    if (typeof window === "undefined") {
        return DEFAULT_HASH_PATH;
    }

    try {
        const current = new URL(window.location.href);
        current.hash = `#${DEFAULT_HASH_PATH}`;
        return current.toString();
    } catch {
        return `${window.location.origin}/#${DEFAULT_HASH_PATH}`;
    }
}

export function getCurrentReturnToUrl(): string {
    if (typeof window === "undefined") {
        return DEFAULT_HASH_PATH;
    }

    try {
        const current = new URL(window.location.href);
        if (isLoginHash(current.hash)) {
            return getDefaultReturnToUrl();
        }
        return current.toString();
    } catch {
        return getDefaultReturnToUrl();
    }
}

export function buildIdLoginUrl(returnTo?: string): string {
    const target = returnTo || getCurrentReturnToUrl();
    const loginUrl = new URL(ID_LOGIN_URL, typeof window !== "undefined" ? window.location.origin : undefined);
    loginUrl.searchParams.set("returnTo", target);
    return loginUrl.toString();
}

export function redirectToIdLogin(returnTo?: string): void {
    const target = buildIdLoginUrl(returnTo);
    window.location.replace(target);
}

let refreshPromise: Promise<boolean> | null = null;

export async function refreshIdSessionOnce(): Promise<boolean> {
    if (refreshPromise) {
        return refreshPromise;
    }

    const fp = getFingerprintFromCookie();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (fp) {
        headers["X-CSRF-Token"] = fp;
    }

    refreshPromise = fetch(`${IDP_ORIGIN}/api/refresh`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
            session_protocol: BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1,
        }),
    })
        .then(async (response) => {
            if (!response.ok) {
                accessToken = null;
                return false;
            }

            let payload: RefreshPayload | null = null;
            try {
                payload = await response.clone().json();
            } catch {
                payload = null;
            }

            setAccessTokenFromPayload(payload);
            return true;
        })
        .catch(() => {
            accessToken = null;
            return false;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
}
